import { assertNever } from "../../../util/assertNever";
import { CommonColumnOperators, NumberColumnOperators } from "../enum.filterOperators";
import { GenericTableFilterType, NumberFilterType } from "../type.genericTableQuery";
import type { FilterMapper } from "./interface.filterMapper";

export class NumberFilterMapper implements FilterMapper {
  toQuery({ filter }: NumberFilterType): undefined | string {
    const option = filter.option;
    switch (option) {
      case NumberColumnOperators.BETWEEN: {
        if (!Array.isArray(filter.value)) {
          throw new Error("between filter requires an array of two numbers as value");
        }
        const [lowerBound, upperBound] = filter.value;
        return `${NumberColumnOperators.BETWEEN}$-$${lowerBound}$-$${upperBound}`;
      }
      case NumberColumnOperators.SMALLER_THEN: {
        const smallerThen = filter.value;
        return `${NumberColumnOperators.SMALLER_THEN}$-$${smallerThen}`;
      }
      case NumberColumnOperators.LARGER_THEN: {
        const largerThen = filter.value;
        return `${NumberColumnOperators.LARGER_THEN}$-$${largerThen}`;
      }
      case NumberColumnOperators.EXACT: {
        const exactNumber = filter.value;
        return `${NumberColumnOperators.EXACT}$-$${exactNumber}`;
      }
      case NumberColumnOperators.IN: {
        const numberArrayValues = filter.value;
        return numberArrayValues ? `${NumberColumnOperators.IN}$-$${numberArrayValues.join(",")}` : undefined;
      }
      case CommonColumnOperators.VALUE_EXISTS: {
        const missingValue = filter.value;
        return `${CommonColumnOperators.VALUE_EXISTS}$-$${missingValue}`;
      }
      default:
        assertNever(option);
    }
  }

  fromQuery(value: string): NumberFilterType {
    const [columnOperator, firstValue, secondValue] = value.split("$-$");
    if (!columnOperator || !firstValue) {
      throw new Error("missing filter value or option");
    }
    const option = this.parseNumberColumnOperator(columnOperator);

    switch (option) {
      case NumberColumnOperators.BETWEEN: {
        const lowerBound = parseFloat(firstValue);
        const upperBound = parseFloat(secondValue ?? "");
        if (isNaN(lowerBound) || isNaN(upperBound)) {
          throw new Error("missing or invalid bounds for BETWEEN filter");
        }
        return {
          type: GenericTableFilterType.NUMBER,
          filter: { option, value: [lowerBound, upperBound] },
        };
      }
      case CommonColumnOperators.VALUE_EXISTS: {
        return {
          type: GenericTableFilterType.NUMBER,
          filter: { option, value: firstValue === "true" },
        };
      }
      case NumberColumnOperators.SMALLER_THEN:
      case NumberColumnOperators.LARGER_THEN:
      case NumberColumnOperators.EXACT: {
        const numValue = parseFloat(firstValue);
        if (isNaN(numValue)) {
          throw new Error("invalid number value for filter");
        }
        return {
          type: GenericTableFilterType.NUMBER,
          filter: { option, value: numValue },
        };
      }
      case NumberColumnOperators.IN: {
        const numberValues = firstValue
          .split(",")
          .map(Number)
          .filter((number) => !isNaN(number));
        return {
          type: GenericTableFilterType.NUMBER,
          filter: { option, value: numberValues },
        };
      }
      default:
        assertNever(option);
    }
  }

  private parseNumberColumnOperator(option: string): CommonColumnOperators | NumberColumnOperators {
    const allOptions = [...Object.values(NumberColumnOperators), ...Object.values(CommonColumnOperators)];
    if (allOptions.includes(option as CommonColumnOperators | NumberColumnOperators)) {
      return option as CommonColumnOperators | NumberColumnOperators;
    }
    throw new Error(`Invalid number filter option: ${option}`);
  }
}
